// src/pages/ContractPrintSlider.tsx
import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from "@mui/material";
import {useParams} from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import {usePeople} from "../../context/PeopleContext";
import {PersonModelInterface} from "../../models/personModel";

const mm = (v: number) => `${v}mm`;

export default function ContractPrintSlider() {
    const {id} = useParams<{ id: string }>();
    const {getPersonById} = usePeople();
    const [person, setPerson] = useState<PersonModelInterface | null>(null);
    const [loading, setLoading] = useState(true);
    const isMobile = useMediaQuery("(max-width:600px)");

    useEffect(() => {
        (async () => {
            try {
                if (id) {
                    const data = await getPersonById(id);
                    setPerson(data);
                }
            } catch (e) {
                console.error("Ошибка при загрузке данных:", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [id, getPersonById]);

    const currentDate = useMemo(() => new Date(), []);

    const calc = () => {
        const {phoneAmount, downPayment, term, percent, releaseDay} =
        person?.creditDetail || {};
        if (!phoneAmount || !downPayment || !term || !percent || !releaseDay) {
            return {schedule: [], info: null as any};
        }
        const remaining = phoneAmount - downPayment;
        const totalAfterPercent = remaining * (1 + percent / 100);
        const total = totalAfterPercent + downPayment;
        const monthlyPayment = totalAfterPercent / term;

        const startDate = new Date(releaseDay);
        const closeDate = new Date(startDate);
        closeDate.setMonth(closeDate.getMonth() + term);

        const schedule: {
            month: number;
            date: string;
            monthlyPayment: number;
            remaining: number;
        }[] = [];
        for (let i = 1; i <= term; i++) {
            const d = new Date(startDate);
            d.setMonth(d.getMonth() + i);
            const paid = monthlyPayment * i;
            schedule.push({
                month: i,
                date: d.toISOString().split("T")[0],
                monthlyPayment: Number(monthlyPayment.toFixed(2)),
                remaining: Number((totalAfterPercent - paid).toFixed(2)),
            });
        }

        return {
            schedule,
            info: {
                total: Number(total.toFixed(2)),
                monthlyPayment: Number(monthlyPayment.toFixed(2)),
                closingDate: closeDate.toISOString().split("T")[0],
            },
        };
    };

    const {schedule, info} = calc();

    const handlePrint = () => window.print();

    if (loading) return <Typography>Загрузка…</Typography>;
    if (!person) return <Typography>Данные о клиенте не найдены</Typography>;

    return (
        <Box
            sx={{
                minHeight: "100dvh",
                bgcolor: "#f5f7fb",
                py: {xs: 2, md: 4},
                "@media print": {
                    bgcolor: "#fff",
                    py: 0,
                },
            }}
        >
            <Box
                sx={{
                    // «карман» со слайдером
                    maxWidth: 1280,
                    mx: "auto",
                    px: {xs: 1.5, md: 3},
                    py: {xs: 2, md: 3},
                    bgcolor: "#fff",
                    borderRadius: 3,
                    boxShadow: "0 10px 30px rgba(16,24,40,.08)",
                    position: "relative",
                    "@media print": {
                        px: 0,
                        py: 0,
                        borderRadius: 0,
                        boxShadow: "none",
                        bgcolor: "#fff",
                    },
                }}
            >
                {/* Горизонтальный превью-слайдер */}
                <Box
                    sx={{
                        display: "flex",
                        overflowX: "auto",
                        scrollSnapType: "x mandatory",
                        gap: 3,
                        pb: 1,
                        "@media print": {
                            display: "block",
                            overflow: "visible",
                            gap: 0,
                            pb: 0,
                        },
                    }}
                >
                    <style>{`
            /* аккуратный незаметный скроллбар */
            ::-webkit-scrollbar{height:8px}
            ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:8px}
            @media print { ::-webkit-scrollbar{display:none} }
          `}</style>

                    {/* ========== СТРАНИЦА 1 ========== */}
                    <Page>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            mb={1}
                            gap={1}
                        >
                            <Typography sx={{fontSize: ".95rem"}}>Бишкек шаары</Typography>
                            <Typography sx={{fontSize: ".95rem"}}>
                                {dayjs(currentDate).locale("ru").format("«DD» MMMM YYYY[-ж.]")}
                            </Typography>
                        </Box>

                        <Typography
                            align="center"
                            sx={{fontWeight: 700, mb: 1, fontSize: {xs: "1.05rem", md: "1.2rem"}}}
                        >
                            ТОВАРДЫ БӨЛҮП ТӨЛӨӨ ШАРТЫНДА САТЫП АЛУУ-САТУУ КЕЛИШИМИ
                        </Typography>

                        <Typography sx={{whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                            Жеке ишкер Сыдыгалиев Адил Нурланович 2025-жылдын 19-февраль
                            003-2025-169-1364, сандуу жеке ишкер күбөлүгүнүн негизинде иш алып баруучу,
                            мындан ары “Сатуучу” деп аталат, бир тараптан, жана Сатып алуучу:
                            {`${person.name}, ${person.detailInfo.city}, ${person.detailInfo.address}, Тел: ${person.detailInfo.phoneNumber}`}
                            {Array.isArray(person.guarantees) && person.guarantees.length > 0 && (
                                <>
                                    {"\n\n"}Кошумча байланыш үчүн:
                                    {person.guarantees.map((g) => (
                                        <React.Fragment key={g.id}>
                                            {"\n"}- {g.name} ({g.role}), Тел: {g.phone}
                                        </React.Fragment>
                                    ))}
                                </>
                            )}
                            {"\n\n"}мындан ары “Сатып алуучу” деп аталат төмөнкү келишимди түзүштү:
                        </Typography>

                        <SectionTitle>1. Келишимдин предмети</SectionTitle>
                        <Typography
                            sx={{whiteSpace: "pre-wrap", lineHeight: 1.4}}>
                            1.1. Бул келишимдин негизинде Сатуучу товарды Сатып алуучунун менчигине өткөрүп берет, ал
                            эми Сатып алуучу товарды кабыл алууга жана анын белгиленген наркын төлөөгө милдеттенет.
                            Товар Сатып алуучуга өткөрүлүп берилгени менен, анын менчигине өтүү укугу толук төлөм
                            жүргүзүлгөндөн кийин гана берилет.
                            {"\n"} 1.2. Товар жөнүндө маалымат:
                            {"\n"}- Товардын аталышы:{" "} {person.phone?.phoneModel || "—"}
                            {"\n"}- Серийный номери: {person.phone?.imei || "—"}
                            {"\n"}- Жалпы баасы: {info?.total ?? person.creditDetail?.total ?? "—"}
                            {"\n"}- Товардын сапаты жана мүнөздөмөсү:{" "} {person.phone?.condition || "—"}
                        </Typography>

                        <SectionTitle>2. Тараптардын милдеттери</SectionTitle>
                        <Typography
                            sx={{whiteSpace: "pre-wrap", lineHeight: 1.4}}>
                            2.1. Сатуучунун милдеттери: {'\n'}
                            2.1.1. Келишимде көрсөтүлгөн товарды сапаттуу жана комплектүү, келишилген баада жана
                            ассортиментте өткөрүп берүү. {'\n'}
                            2.1.2. Товарды өткөрүп берүү менен кошо тиешелүү документтерди тапшыруу:
                            Гарантиялык талон,
                            Сертификат,
                            Келишимдин негизинде түзүлгөн эки тарап кол койгон кабыл алуу актысы.
                            {'\n\n'} 2.2. Сатып алуучунун милдеттери:
                            {'\n'}2.2.1. Товарды кабыл алуу. {'\n'}
                            2.2.2. Товар үчүн келишимде көрсөтүлгөн баада жана мөөнөттө төлөө. {'\n'}
                            2.2.3. Товарда кемчиликтер же келишим шарттарына дал келбеген учурлар аныкталса, 14 күндүн
                            ичинде Сатуучуга жазуу жүзүндө билдирүү.
                        </Typography>

                        <SectionTitle>3. Товардын баасы жана төлөм шарттары</SectionTitle>
                        <Typography sx={{whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                            3.1. Товардын баасы спецификацияда көрсөтүлгөн товар түрүнө жараша аныкталат.
                            {'\n'}3.2. Товар үчүн бөлүп төлөө шартында эсептешүү жүргүзүлөт.
                            {'\n'}3.3. Келишим түзүлгөн учурда Сатып алуучу баштапкы төлөм катары 30%/ 50% төлөйт.
                            Калган сумма 3 айдын же 6 айдын ичинде төлөнөт.
                            {'\n'}3.4. Калган сумма ай сайын бирдей төлөмдөр менен төмөнкү өлчөмдө төлөнөт:
                            {'\n'}_% товар баасынан, ар бир
                            айдын {dayjs(currentDate).locale("ru").format("«DD» MMMM YYYY[-ж.]")} күнү кечиктирилбестен.
                            {'\n'}3.5. Төлөм Сатуучунун банк эсебине акча которуу жолу менен жүргүзүлөт.
                            {'\n'}3.6. Төлөмдүн графиги:
                        </Typography>
                    </Page>

                    {/* ========== СТРАНИЦА 2 ========== */}
                    <Page>
                        {info && (
                            <Box sx={{mt: 2}}>
                                <Typography>- Жалпы суммасы: {info.total} сом</Typography>
                                <Typography>- Ай сайын төлөм: {info.monthlyPayment} сом</Typography>
                                <Typography>
                                    - Жабылуу күнү: {dayjs(info.closingDate).locale("ru").format("DD.MM.YYYY")}
                                </Typography>
                            </Box>
                        )}

                        {schedule.length > 0 && (
                            <Box sx={{mt: 2}}>
                                <TableContainer>
                                    <Table size="small" sx={{borderCollapse: "collapse"}}>
                                        <TableHead>
                                            <TableRow>
                                                {["Ай", "Төлөө күнү", "Ай сайын төлөм", "Калды"].map((h) => (
                                                    <TableCell key={h}
                                                               sx={{border: "1px solid #e5e7eb", fontWeight: 600}}>
                                                        {h}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {schedule.map((row) => (
                                                <TableRow key={row.month}>
                                                    <TableCell
                                                        sx={{border: "1px solid #e5e7eb"}}>{row.month}</TableCell>
                                                    <TableCell sx={{border: "1px solid #e5e7eb"}}>
                                                        {dayjs(row.date).locale("ru").format("DD.MM.YYYY")}
                                                    </TableCell>
                                                    <TableCell sx={{border: "1px solid #e5e7eb"}}>
                                                        {row.monthlyPayment} сом
                                                    </TableCell>
                                                    <TableCell sx={{border: "1px solid #e5e7eb"}}>
                                                        {row.remaining} сом
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        <SectionTitle>4. Жоопкерчилик</SectionTitle>
                        <Typography sx={{whiteSpace: "pre-wrap", lineHeight: 1.4}}>
                            4.1. Сатуучу товарды өткөрүп бергенге чейин болгон кемчиликтер же бузулуулар үчүн
                            жоопкерчилик тартат.
                            Товарды текшерүгө 14 күн берилет
                            {"\n"}
                            4.2. Төлөм болбосо — келишимди токтотуп, товарды кайтарып алуу/дистанциялык бөгөт коюу укугу
                            бар.
                        </Typography>

                        <SectionTitle>5. Жыйынтыктоочу жоболор</SectionTitle>
                        <Typography sx={{whiteSpace: "pre-wrap", lineHeight: 1.5}}>
                            5.1. Сатуучу, Сатып алуучуга товарды өткөрүп берген учурдан тартып, өз милдеттенмесин
                            аткарган болуп эсептелет.
                            {'\n'}5.2. Товардын кокустан бузулуу же жоголуу коркунучу Сатып алуучуга товар өткөрүлгөн
                            учурдан тартып өтөт.
                            {'\n'}5.3. Товар толук төлөнүп бүткөнгө чейин Сатуучунун күрөөсүндө (залогунда) болот жана
                            Сатып алуучу аны сатууга, үчүнчү тараптарга өткөрүп берүүгө же күрөөгө коюуга укугу жок.
                            {'\n'}5.4. Бул келишимде каралбаган башка маселелер боюнча Кыргыз Республикасынын мыйзамдары
                            колдонулат.
                            {'\n'}5.5. Келишим эки нускада түзүлөт, ар бир тарабы бир нускадан алат жана бирдей
                            юридикалык күчкө ээ
                            {'\n'}5.6. Сатып алуучу телефонду толук төлөп бүткөнгө чейин аны пайдалануучу катары гана
                            колдонууга укуктуу, анын программалык камсыздоосун өзгөртүүгө, ачууга же башка жолдор менен
                            модификациялоого тыюу салынат.
                            {'\n'}5.7. Эгерде келишим боюнча талаш-тартыштар пайда болсо, алар Сатуучунун жайгашкан
                            жериндеги сотто каралат (сотко караштуулук Сатуучунун орду боюнча аныкталат).
                        </Typography>

                        <SectionTitle>6. Тараптардын реквизиттери жана кол тамгалары</SectionTitle>
                        <RequisitesFlex person={person}/>

                    </Page>

                    {/* ========== СТРАНИЦА 3 — БЛАНК ========== */}
                    <Page>
                        <Typography align="right" sx={{mb: 1}}>
                            Бишкек ш.
                        </Typography>
                        <SectionTitle>Сатып алуучу тууралуу маалымат</SectionTitle>

                        {[
                            {n: 1, label: "Ф.И.О", value: person.name},
                            {
                                n: 2,
                                label: "Туулган жылы, айы, күнү",
                                value: "",
                            },
                            {n: 3, label: "Иштеген жери", value: ""},
                            {
                                n: 4,
                                label: "Мекеме жана кызмат орду",
                                value: "",
                            },
                            {
                                n: 5,
                                label: "Жашаган жери",
                                value: "",
                            },
                        ].map((row) => (
                            <LinedField key={row.n} n={row.n} label={row.label} value={row.value}/>
                        ))}

                        <Typography sx={{mt: 2, lineHeight: 1.6}}>
                            Мен <Underline>{person.name}</Underline>, Жеке ишкер Сыдыгалиев Адил
                            Нурлановичтен болуп толоо жолу
                            менен <Underline>{`${person?.phone?.phoneModel} ${person?.phone?.condition}`} үлгүсүндөгү
                            телефонду</Underline>{" "}
                            алып жатып, өзүм тууралуу маалыматты тактоого жана паспорттогу
                            маалыматты текшерүүгө макулдугумду берем.
                        </Typography>

                        <Box sx={{my: 2}}>
                            {Array.from({length: 4}).map((_, i) => (
                                <Box key={i} sx={{borderBottom: "3px solid #111827", height: 18, mb: 1}}/>
                            ))}
                        </Box>

                        <Box sx={{display: "flex", justifyContent: "space-between", mt: 3, mb: 4}}>
                            <Typography>{dayjs(currentDate).format("DD.MM.YYYY")} ж.</Typography>
                        </Box>

                        <Box sx={{display: "flex", gap: 6, mt: 2}}>
                            <Typography>Ф.И.О: ______________________</Typography>
                            <Typography>Колу: ______________________</Typography>
                        </Box>
                    </Page>
                </Box>

                <Box
                    sx={{
                        position: "sticky",
                        bottom: 12,
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                        pointerEvents: "none",
                        "@media print": {display: "none"},
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={handlePrint}
                        sx={{
                            pointerEvents: "auto",
                            px: 3,
                            py: 1,
                            borderRadius: 2,
                            boxShadow: "0 8px 20px rgba(59,130,246,.35)",
                            "@media print": {display: "none"},
                        }}
                    >
                        Печать
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

/** ----- Мелкие компоненты для аккуратной вёрстки ----- */

function Page({children}: { children: React.ReactNode }) {
    return (
        <Paper
            elevation={4}
            sx={{
                flex: "0 0 auto",
                width: {xs: "92vw", md: 760},
                minHeight: {xs: "auto", md: 1074},
                aspectRatio: {xs: "210 / 297", md: "auto"},
                p: {xs: 2, md: 3},
                borderRadius: 2.5,
                boxShadow: "0 6px 24px rgba(16,24,40,.12)",
                scrollSnapAlign: "start",
                background: "#fff",
                "@media print": {
                    width: mm(210),
                    minHeight: mm(297),
                    p: mm(10),
                    borderRadius: 0,
                    boxShadow: "none",
                    breakAfter: "page",
                    pageBreakAfter: "always",
                    "&:last-child": {
                        breakAfter: "auto",
                        pageBreakAfter: "auto",
                    },
                },
            }}
        >
            {children}
        </Paper>
    );
}


function SectionTitle({children}: { children: React.ReactNode }) {
    return (
        <Typography align="center" sx={{fontWeight: 700, mt: 2, mb: 1}}>
            {children}
        </Typography>
    );
}

function LinedField({
                        n,
                        label,
                        value,
                    }: {
    n: number;
    label: string;
    value?: string;
}) {
    return (
        <Box sx={{mb: 1.75}}>
            <Typography sx={{mb: 0.5}}>{n})</Typography>
            <Box
                sx={{
                    borderBottom: "1px solid #111827",
                    height: 22,
                    position: "relative",
                    mb: 0.5,
                    borderRadius: 0.5,
                }}
            >
                <Typography
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        px: 1,
                    }}
                >
                    {value}
                </Typography>
            </Box>
            <Typography align="center" sx={{color: "text.secondary", fontSize: ".9rem"}}>
                {label}
            </Typography>
        </Box>
    );
}

function Underline({children}: { children: React.ReactNode }) {
    return (
        <span style={{borderBottom: "1px solid #111827", padding: "0 6px"}}>
      {children}
    </span>
    );
}

export function RequisitesFlex({person}: { person: PersonModelInterface }) {
    return (
        <Box
            sx={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
                mt: 2,
                "@media print": {gap: 1},
            }}
        >
            {/* Колонка: Сатуучу */}
            <Typography
                sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                    flex: "1 1 360px",
                    minWidth: 280,
                }}
            >{`Сатуучу:
ИП: Сыдыгалиев Адил Нурланович
ИНН: 21110200100743
Дарек: Кыргызская Республика, Нарынская обл, г.Нарын, ул.Ленин, дом 146, кв.3
Байланыш: 0509206060-W/A, MBANK Бексултан.А
Колу: __________`}</Typography>

            {/* Колонка: Сатып алуучу */}
            <Typography
                sx={{
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                    flex: "1 1 360px",
                    minWidth: 280,
                }}
            >{`Сатып алуучу:
Аты-жөнү: ${person.name || "________"}
ИНН: ${person.detailInfo?.individualNumber || "________"}
Дарек: ${person.detailInfo?.address || "________"}
Байланыш: ${person.detailInfo?.phoneNumber || "________"}
Колу: __________`}</Typography>
        </Box>
    );
}


